/**
 * useCreateSecret Hook
 *
 * Custom React Query mutation hook for creating secrets.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { secretService } from '../../../api/secret.service';
import type { SecretCreateDTO } from '../../../types/secret.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateSecretOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating secret
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useCreateSecret = (tenantId: string, options?: UseCreateSecretOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SecretCreateDTO) => secretService.createSecret(tenantId, data),
    onSuccess: () => {
      // Invalidate all secret queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['secrets', tenantId],
        exact: false
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
